import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Alert } from "react-daisyui";

import { useStepperContext } from "@/components/shared/Stepper/StepperContext.tsx";
import TripCreateStep, {
  allTripCreateSteps,
} from "@/_constants/TripCreateStep.ts";
import { tripCreateSchema } from "$/requests/TripCreateRequest.ts";
import type { TripData } from "$/models/Trip.model.ts";
import type { OnCompleteArgs } from "@/components/functional/TripManipulationForm/TripManipulationForm.tsx";
import LoadingButton from "@/components/functional/LoadingButton/LoadingButton.tsx";
import ChevronRightIcon from "@/components/ui/icons/ChevronRightIcon.tsx";
import AutocompleteInput from "@/components/functional/AutocompleteInput/AutocompleteInput.tsx";
import requiresDestinationPick from "@/utils/requiresDestinationPick.ts";
import initialPlanningType, {
  shouldSaveDestinationStep,
} from "@/utils/trip/initialPlanningType.ts";
import StepIndicator from "@/components/ui/StepIndicator/StepIndicator.tsx";
import TripPlanningType, {
  allTripPlanningTypes,
  type TripPlanningType as TripPlanningTypeValue,
} from "$/_const/trip/TripPlanningType.ts";
import TripLayout from "@/layouts/TripLayout/TripLayout.tsx";
import {
  TripLayoutProvider,
  useTripLayoutContext,
} from "@/layouts/TripLayout/TripLayoutProvider.tsx";

const destinationSchema = tripCreateSchema
  .pick({
    trip_planning_type: true,
    trip_destination: true,
    trip_destination_place_id: true,
    trip_mood: true,
  })
  .superRefine((data, context) => {
    if (data.trip_planning_type === TripPlanningType.HELP_ME_DECIDE) {
      if (!data.trip_mood?.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["trip_mood"],
          message: "Tell us what kind of trip you are in the mood for.",
        });
      }

      return;
    }

    if (!data.trip_destination?.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["trip_destination"],
        message: "Enter a destination.",
      });
    }
  });

export type DestinationSchema = z.infer<typeof destinationSchema>;

type DestinationProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

const planningTypeDescriptions: Record<
  TripPlanningTypeValue,
  {
    titleKey: string;
    descriptionKey: string;
  }
> = {
  [TripPlanningType.DESTINATION_IN_MIND]: {
    titleKey:
      "tripCreateForm.destination.planningTypes.destinationInMind.title",
    descriptionKey:
      "tripCreateForm.destination.planningTypes.destinationInMind.description",
  },
  [TripPlanningType.MULTI_STOP]: {
    titleKey: "tripCreateForm.destination.planningTypes.multiStop.title",
    descriptionKey:
      "tripCreateForm.destination.planningTypes.multiStop.description",
  },
  [TripPlanningType.ROAD_TRIP]: {
    titleKey: "tripCreateForm.destination.planningTypes.roadTrip.title",
    descriptionKey:
      "tripCreateForm.destination.planningTypes.roadTrip.description",
  },
  [TripPlanningType.HELP_ME_DECIDE]: {
    titleKey: "tripCreateForm.destination.planningTypes.helpMeDecide.title",
    descriptionKey:
      "tripCreateForm.destination.planningTypes.helpMeDecide.description",
  },
};

function DestinationNavigation() {
  const { isMenuOpen, setIsMenuOpen } = useTripLayoutContext();

  return (
    <div
      className={`fixed inset-0 z-50 ${
        isMenuOpen
          ? "visible pointer-events-auto"
          : "invisible pointer-events-none"
      }`}
      aria-hidden={!isMenuOpen}
    >
      <button
        type="button"
        aria-label="Close navigation menu"
        className={`absolute inset-0 bg-black/45 transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      <TripLayout.NavigationPanel
        className={`absolute inset-y-0 left-0 transition-transform duration-300 ease-out ${
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      />
    </div>
  );
}

function Destination({
  onComplete,
  onCompleteWhenDirty,
}: DestinationProps) {
  const { t } = useTranslation();

  const {
    nextStep,
    setContextData,
    contextData,
    currentStepIndex,
    steps,
  } = useStepperContext<typeof allTripCreateSteps, TripData>();

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isAutocompleteReady, setIsAutocompleteReady] = useState(false);
  const [loadedTrip] = useState(() => ({
    planningType: contextData?.trip_planning_type,
    placeId: contextData?.trip_destination_place_id ?? null,
    destination: contextData?.trip_destination ?? null,
  }));

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError: setFieldError,
    clearErrors,
    formState: { isDirty },
  } = useForm<DestinationSchema>({
    resolver: zodResolver(destinationSchema),
    defaultValues: {
      trip_planning_type: initialPlanningType(contextData),
      trip_destination: contextData?.trip_destination ?? null,
      trip_destination_place_id: contextData?.trip_destination_place_id ?? null,
      trip_mood: contextData?.trip_mood ?? null,
    },
  });

  const planningType = watch("trip_planning_type");
  const isHelpMeDecide = planningType === TripPlanningType.HELP_ME_DECIDE;
  const isComingSoon = !!planningType &&
    planningType !== TripPlanningType.DESTINATION_IN_MIND;

  const selectPlanningType = (
    option: TripPlanningTypeValue,
  ) => {
    setValue("trip_planning_type", option, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    if (option === TripPlanningType.HELP_ME_DECIDE) {
      setValue("trip_destination", null, {
        shouldDirty: true,
        shouldValidate: false,
      });
    } else {
      setValue("trip_mood", null, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }

    clearErrors([
      "trip_planning_type",
      "trip_destination",
      "trip_mood",
    ]);
  };

  const onSubmit = (data: DestinationSchema) => {
    if (
      data.trip_planning_type !==
        TripPlanningType.DESTINATION_IN_MIND
    ) {
      return;
    }

    if (
      requiresDestinationPick({
        planningType: data.trip_planning_type,
        isAutocompleteReady,
        placeId: data.trip_destination_place_id,
        destination: data.trip_destination,
        loadedPlaceId: loadedTrip.placeId,
        loadedDestination: loadedTrip.destination,
      })
    ) {
      setFieldError("trip_destination", {
        type: "manual",
        message: t("tripCreateForm.destination.pickFromSuggestions"),
      });
      return;
    }

    void (async () => {
      try {
        setIsPending(true);
        setError(null);

        const cleanData: DestinationSchema = isHelpMeDecide
          ? {
            ...data,
            trip_destination: null,
            trip_destination_place_id: null,
            trip_mood: data.trip_mood?.trim() ?? null,
          }
          : {
            ...data,
            trip_destination: data.trip_destination?.trim() ?? null,
            trip_destination_place_id: data.trip_destination_place_id ?? null,
            trip_mood: null,
          };

        const context: TripData = {
          ...contextData,
          ...cleanData,
        };

        const args: OnCompleteArgs = {
          step: TripCreateStep.DESTINATION,
          data: cleanData,
          context,
        };

        const shouldSave = shouldSaveDestinationStep({
          isDirty,
          loadedPlanningType: loadedTrip.planningType,
          submittedPlanningType: cleanData.trip_planning_type,
        });

        if (shouldSave) {
          setContextData((previous) => ({
            ...previous,
            ...cleanData,
          }));

          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        const nextError = caughtError instanceof Error
          ? caughtError
          : new Error(String(caughtError));

        setError(nextError);
        console.error(nextError);
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <TripLayoutProvider>
      <DestinationNavigation />

      <div
        className="min-h-[100dvh] max-w-none overflow-x-hidden bg-journi-theme-light text-[#191919]"
        style={{
          position: "relative",
          left: "50%",
          width: "100vw",
          minWidth: "100vw",
          maxWidth: "none",
          transform: "translateX(-50%)",
        }}
      >
        <header className="flex w-full items-center justify-between gap-6 border-b border-primary/60 px-5 py-5 sm:px-8 md:px-14 lg:px-20">
          <div className="flex items-center gap-4">
            <TripLayout.OpenMenuButton color="primary" />

            <span className="font-feature text-3xl font-bold">
              Journi
            </span>
          </div>

          <StepIndicator
            numberOfSteps={steps.length}
            currentStepIndex={currentStepIndex}
          />
        </header>

        <main className="w-full px-5 py-8 sm:px-8 md:px-[5vw] md:py-10">
          <form
            className="flex w-full flex-col"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            {error
              ? (
                <Alert status="error" className="mb-6">
                  {error.message}
                </Alert>
              )
              : null}

            <p className="text-sm font-semibold uppercase tracking-wide text-primary">
              {t("tripCreateForm.destination.eyebrow")}
            </p>

            <h1 className="mt-1 font-feature text-4xl font-bold leading-tight md:text-5xl">
              {t("tripCreateForm.destination.headerPrefix")}{" "}
              <span className="italic text-primary">
                {t(
                  "tripCreateForm.destination.headerHighlight",
                )}
              </span>
            </h1>

            <Controller
              control={control}
              name="trip_planning_type"
              render={({ fieldState }) => (
                <div className="mt-7">
                  <div className="mx-auto grid w-full max-w-[560px] grid-cols-1 gap-y-6 md:w-[74vw] md:max-w-[1380px] md:grid-cols-2 md:gap-x-[clamp(24px,3vw,56px)] md:gap-y-[clamp(22px,2.5vw,36px)]">
                    {allTripPlanningTypes.map((option) => {
                      const optionCopy = planningTypeDescriptions[option];

                      const isSelected = planningType === option;
                      const optionIsComingSoon = option !==
                        TripPlanningType.DESTINATION_IN_MIND;

                      return (
                        <button
                          key={option}
                          type="button"
                          aria-pressed={isSelected}
                          disabled={isPending}
                          className={[
                            "box-border flex w-full flex-col justify-center overflow-hidden rounded-[18px] border px-6 py-5 text-left transition-colors",
                            isSelected
                              ? "border-primary bg-primary/15 text-primary"
                              : "border-primary bg-white text-[#191919] hover:bg-primary/5",
                          ].join(" ")}
                          style={{
                            height: "clamp(140px, 11vw, 190px)",
                          }}
                          onClick={() => selectPlanningType(option)}
                        >
                          <span className="block text-lg font-semibold uppercase">
                            {t(optionCopy.titleKey)}
                          </span>

                          {isSelected && optionIsComingSoon
                            ? (
                              <span className="mt-4 inline-flex w-fit items-center rounded-full border border-primary bg-primary px-4 py-2 text-sm font-bold uppercase tracking-[0.08em] text-[#191919] shadow-[0_6px_16px_rgba(201,168,76,0.28)]">
                                Coming soon
                              </span>
                            )
                            : (
                              <span className="mt-2 block min-h-[40px] text-sm font-semibold leading-5 text-text-secondary">
                                {t(optionCopy.descriptionKey)}
                              </span>
                            )}
                        </button>
                      );
                    })}
                  </div>

                  {fieldState.error
                    ? (
                      <p
                        className="mt-3 text-sm font-semibold text-error"
                        role="alert"
                      >
                        {fieldState.error.message}
                      </p>
                    )
                    : null}
                </div>
              )}
            />

            {planningType
              ? (
                <div className="mx-auto mt-16 w-full md:w-[90vw] md:max-w-[1600px]">
                  {isHelpMeDecide
                    ? (
                      <Controller
                        control={control}
                        name="trip_mood"
                        render={({ field, fieldState }) => (
                          <div>
                            <label
                              htmlFor="trip-mood"
                              className="font-feature text-3xl font-bold"
                            >
                              {t(
                                "tripCreateForm.destination.moodLabel",
                              )}
                            </label>

                            <textarea
                              id="trip-mood"
                              rows={5}
                              value={field.value ?? ""}
                              disabled={isPending}
                              placeholder={t(
                                "tripCreateForm.destination.moodPlaceholder",
                              )}
                              className="mt-4 w-full resize-y rounded-[14px] border border-primary bg-white px-4 py-4 text-base font-semibold text-[#191919] outline-none placeholder:text-text-secondary focus:ring-2 focus:ring-primary/30"
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            />

                            {fieldState.error
                              ? (
                                <p
                                  className="mt-2 text-sm font-semibold text-error"
                                  role="alert"
                                >
                                  {fieldState.error.message}
                                </p>
                              )
                              : null}
                          </div>
                        )}
                      />
                    )
                    : (
                      <Controller
                        control={control}
                        name="trip_destination"
                        render={({ field, fieldState }) => (
                          <div>
                            <label className="font-feature text-3xl font-bold">
                              {t(
                                "tripCreateForm.destination.destinationLabel",
                              )}
                            </label>

                            <div className="mt-4 rounded-[14px] border border-primary bg-white p-[1px] shadow-[0_5px_16px_rgba(201,168,76,0.12)] transition-shadow focus-within:shadow-[0_0_0_4px_rgba(201,168,76,0.18)]">
                              <AutocompleteInput
                                includedPrimaryTypes={[
                                  "locality",
                                  "sublocality",
                                  "neighborhood",
                                  "administrative_area_level_1",
                                  "administrative_area_level_2",
                                ]}
                                preserveSelectionText
                                error={fieldState.error?.message}
                                value={field.value ?? ""}
                                onChange={field.onChange}
                                onPlaceSelect={(selection) =>
                                  setValue(
                                    "trip_destination_place_id",
                                    selection?.placeId ?? null,
                                    { shouldDirty: true },
                                  )}
                                onReadyChange={setIsAutocompleteReady}
                                placeholder={t(
                                  "tripCreateForm.destination.destinationPlaceholder",
                                )}
                              />
                            </div>
                          </div>
                        )}
                      />
                    )}
                </div>
              )
              : null}

            <div className="mt-9 flex justify-center">
              <LoadingButton
                loading={isPending}
                disabled={isPending || isComingSoon}
                type="submit"
                color="primary"
                endIcon={<ChevronRightIcon className="h-3.5 w-3.5" />}
                className={[
                  "w-full sm:w-[250px]",
                  isComingSoon
                    ? "!border-[#B8B4AC] !bg-none !bg-[#D8D4CC] !text-[#77736B] !shadow-none disabled:cursor-not-allowed disabled:opacity-100"
                    : "journi-primary-button",
                ].join(" ")}
              >
                {t("button.continue")}
              </LoadingButton>
            </div>
          </form>
        </main>
      </div>
    </TripLayoutProvider>
  );
}

export default Destination;
